"use client";

import {
  ButtonHTMLAttributes,
  HTMLAttributes,
  MouseEventHandler,
  SVGProps,
  useId,
  useEffect,
  useState,
} from "react";
import { createPortal } from "react-dom";

import CloseIcon from "@/assets/icons/close.svg?react";
import { mergeRefs } from "@/lib/mergeRefs";
import { cn } from "@/lib/tailwind";
import { useFocusTrap } from "./hooks/useFocusTrap";
import { useModal } from "./hooks/useModal";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  slots?: {
    root?: HTMLAttributes<HTMLDivElement>;
    backdrop?: HTMLAttributes<HTMLDivElement>;
    container?: HTMLAttributes<HTMLDivElement>;
    close?: {
      visible?: boolean;
      icon?: SVGProps<SVGElement>;
      container?: ButtonHTMLAttributes<HTMLButtonElement>;
    };
  };
}

const Modal = ({
  isOpen,
  onClose,
  children,
  slots = {
    root: {},
    backdrop: {},
    container: {},
    close: { visible: true },
  },
}: IProps) => {
  const modalId = useId();
  const focusTrapRef = useFocusTrap(isOpen);
  const { modalRef } = useModal({ isOpen, onClose });

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const { backdrop = {}, close = { visible: true }, container = {}, root = {} } = slots;

  const handleOnClose: MouseEventHandler<HTMLButtonElement | HTMLDivElement> = (e) => {
    e.preventDefault();
    onClose();
  };

  if (!isOpen || !mounted) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalId}
      {...root}
      className={cn("fixed inset-0 z-[20] flex items-center justify-center px-4", root.className)}
    >
      {/* Backdrop */}
      <div
        aria-hidden="true"
        onClick={handleOnClose}
        {...backdrop}
        className={cn("absolute inset-0 bg-black/50 backdrop-blur-sm", backdrop.className)}
      />
      {/* Modal Content */}
      <div
        tabIndex={-1}
        ref={mergeRefs(modalRef, focusTrapRef)}
        {...container}
        className={cn(
          "relative w-max bg-white shadow-xl focus:outline-none focus-visible:ring-1 focus-visible:ring-black",
          close.visible && "p-4",
          container.className,
        )}
      >
        {close.visible && (
          <button
            onClick={handleOnClose}
            {...close.container}
            className={cn(
              "mb-2 ml-auto flex cursor-pointer justify-end",
              close.container?.className,
            )}
          >
            <CloseIcon {...close.icon} className={cn("h-3 w-3", close.icon?.className)} />
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
